import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { useQueryClient } from '@tanstack/react-query';
import { Row, UnmatchedFilterValue } from 'talent_matching/types';

type QueryHelper = {
  excludePeople: (candidateIds: number[]) => void;
  invalidatePeople: (options?: { refetch: boolean }) => void;
  invalidateCandidate: (id: number) => void;
};

const useQueryHelper = (): QueryHelper => {
  const { setValue, getValues } = useFormContext();
  const queryClient = useQueryClient();

  const excludePeople = (candidateIds: number[]) => {
    const excludedSet = new Set(candidateIds);

    queryClient.setQueriesData<{
      rows: Row[];
      buffer: Row[];
      totalRows: number;
      unmatchedFilterValues?: UnmatchedFilterValue[];
    }>({ queryKey: ['people'] }, (oldData) => {
      if (!oldData) {
        return oldData;
      }
      const filteredRows = oldData.rows.filter(
        (row) => !excludedSet.has(row.id)
      );
      const removedCount = oldData.rows.length - filteredRows.length;
      const buffer = oldData.buffer ?? [];
      const replacements = buffer.slice(0, removedCount);
      return {
        ...oldData,
        rows: [...filteredRows, ...replacements],
        buffer: buffer.slice(replacements.length),
        totalRows: oldData.totalRows - removedCount,
      };
    });

    const prevExcludedIds =
      (getValues('excludedIds') as number[] | undefined) ?? [];
    setValue('excludedIds', [
      ...new Set([...prevExcludedIds, ...candidateIds]),
    ]);

    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: ['people'],
        refetchType: 'none',
      });
    }, 0);
  };

  const invalidatePeople = (options?: { refetch: boolean }) => {
    queryClient.invalidateQueries({
      queryKey: ['people'],
      refetchType: options?.refetch === false ? 'none' : 'active',
    });
  };

  const invalidateCandidate = (id: number) => {
    queryClient.invalidateQueries({
      queryKey: ['person', 'panel', id],
    });
  };

  return {
    excludePeople,
    invalidatePeople,
    invalidateCandidate,
  };
};

export default useQueryHelper;
